import { useMemo, useState } from 'react'
import { Box } from '@mui/material'
import {
  SchedulerPackagingRoutesRowType,
  SchedulerTabsProps,
  TrialRowType,
  WorkstationDownTimeRowType,
} from './Scheduler.types'
import { useDeclaredTanks, withoutTanks } from './tankRoutes'
import {
  useScheduleProductionRequirements,
  useSchedulerTableHandlers,
} from './Scheduler.data'
import { SchedulerWorkstationDownTimeTable } from './SchedulerWorkstationDownTime.muiTable'
import { SchedulerTrialsTable } from './SchedulerTrials.muiTable'
import { FONT_SANS, TogSwitch, TOKENS } from './SchedulerDesign'
import { WeekSelector } from './WeekSelector.view'

export const ScheduleWorkstationDowntime = ({
  schedulerId,
  iteration,
}: SchedulerTabsProps) => {
  const API = 'workstation-downtime'
  const TRIALS_API = 'trials'
  const [selectedWeek, setSelectedWeek] = useState(1)

  // Trials share this tab because that is where the client asked to enter them
  // — but they are their own table (their own Delta table, too: a trial row on
  // the downtime table would overwrite the resource's real downtime). Defaults
  // to hidden so the ordinary downtime flow stays uncluttered, and defaults to
  // shown whenever the week already has trials, so hidden data doesn't surprise
  // anyone — but the toggle always wins over that default, so the original
  // downtime table stays reachable. `null` means "no explicit choice yet, use
  // the default"; resets to `null` on every week change so the next week picks
  // its own default rather than inheriting the previous week's manual choice.
  const [trialsOverride, setTrialsOverride] = useState<boolean | null>(null)

  const { data: responseData = { data: [], predicate: undefined }, isLoading } =
    useScheduleProductionRequirements(schedulerId, API, iteration, selectedWeek)
  const { data = [], predicate } = responseData

  const {
    updateTableData,
    uploadScheduleData,
    deleteTableData,
    isSaving,
    isUploading,
    isDeleting,
    updateRowsStatus,
  } = useSchedulerTableHandlers(schedulerId, API, iteration, selectedWeek)

  // ─── Trials ──────────────────────────────────────────────────────────────
  const {
    data: trialsResponse = { data: [], predicate: undefined },
    isLoading: trialsLoading,
  } = useScheduleProductionRequirements(
    schedulerId,
    TRIALS_API,
    iteration,
    selectedWeek
  )
  const serverTrials = (trialsResponse.data ?? []) as TrialRowType[]
  const hasServerTrials = serverTrials.length > 0
  const showTrials = trialsOverride ?? hasServerTrials

  const trialHandlers = useSchedulerTableHandlers<TrialRowType>(
    schedulerId,
    TRIALS_API,
    iteration,
    selectedWeek
  )

  // Rows added here live locally until their first edit saves them, the same
  // way the steriliser-routing table adds rows. Negative ids keep them clear of
  // anything the server assigned.
  const [newTrials, setNewTrials] = useState<TrialRowType[]>([])
  // Local rows drop out as soon as the server has them.
  //
  // `newTrials` is never cleared on save, so without this filter a trial
  // added in this session appeared TWICE after saving — once from the
  // server with the edit, once from stale local state without it — and the
  // stale copy is what the planner saw. That is the "sometimes the time
  // windows don't save" report: it saved, and the pre-edit copy was still
  // on screen next to it.
  const trials = useMemo(() => {
    const onServer = new Set(serverTrials.map((t) => String(t.trialId)))
    return [
      ...serverTrials,
      ...newTrials.filter((t) => !onServer.has(String(t.trialId))),
    ]
  }, [serverTrials, newTrials])

  const addTrial = () => {
    const id = -(newTrials.length + 1)
    // A trial id must be UNIQUE, not merely sequential: the save merges on
    // `source.trialId = target.trialId`, so a regenerated id updates the
    // existing row instead of inserting a new one. Deriving it from a count
    // (`serverTrials.length + …`) reused an id as soon as anything was
    // deleted. Take the first number nothing is using.
    const taken = new Set([
      ...serverTrials.map((t) => String(t.trialId)),
      ...newTrials.map((t) => String(t.trialId)),
    ])
    let n = 1
    while (taken.has(`TRIAL_${n}`)) n++
    setNewTrials((rows) => [
      ...rows,
      {
        id,
        trialId: `TRIAL_${n}`,
        trialName: '',
        line: '',
        lineWindow: '',
        requiresKitchen: false,
        kitchen: '',
        kitchenWindow: '',
        productCode: '',
        active: true,
      },
    ])
  }

  // Packing routes are the only table that lists which kitchens and lines the
  // plant actually has, so the two pickers offer real resources rather than
  // free text the engine would reject. Fetched only once the trials table is on
  // screen — it is large and irrelevant to the downtime flow.
  const { data: routesResponse } = useScheduleProductionRequirements(
    schedulerId,
    'packing-routes',
    iteration,
    undefined,
    showTrials
  )
  // A declared tank in a Kitchen / Line cell is a vessel, not a unit a trial
  // can run on (tankRoutes.ts).
  const declaredTanks = useDeclaredTanks(schedulerId, iteration, showTrials)
  const { lineOptions, kitchenOptions } = useMemo(() => {
    const rows = withoutTanks(
      (routesResponse?.data ?? []) as SchedulerPackagingRoutesRowType[],
      declaredTanks
    )
    const lines = new Set<string>()
    const kitchens = new Set<string>()
    for (const r of rows) {
      if (r?.kitchen) kitchens.add(r.kitchen)
      for (const line of [
        r?.packingLine1,
        r?.packingLine2,
        r?.packingLine3,
        r?.packingLine4,
      ]) {
        if (line) lines.add(line)
      }
    }
    return {
      lineOptions: Array.from(lines).sort(),
      kitchenOptions: Array.from(kitchens).sort(),
    }
  }, [routesResponse, declaredTanks])

  return (
    <Box
      sx={{
        height: 'calc(100vh - 240px)',
        minHeight: 400,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <WeekSelector
          value={selectedWeek}
          onChange={(w) => {
            setSelectedWeek(w)
            setTrialsOverride(null)
          }}
        />
        <Box
          title={
            showTrials
              ? 'Showing trials. Switch off to view the workstation downtime table.'
              : hasServerTrials
                ? 'This week has trials. Switch on to view them.'
                : 'Switch on to show the trials table: experimental production in a fixed window on a line, a kitchen, or both.'
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mb: 1.5,
          }}
        >
          <TogSwitch on={showTrials} onChange={setTrialsOverride} />
          <Box
            component="button"
            onClick={() => setTrialsOverride(!showTrials)}
            sx={{
              border: 'none',
              background: 'none',
              p: 0,
              fontSize: 12,
              fontWeight: 700,
              color: showTrials ? TOKENS.ink700 : TOKENS.ink500,
              fontFamily: FONT_SANS,
              cursor: 'pointer',
            }}
          >
            Trials
          </Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {showTrials ? (
          <SchedulerTrialsTable
            data={trials}
            isLoading={trialsLoading}
            updateTableData={trialHandlers.updateTableData}
            uploadScheduleData={trialHandlers.uploadScheduleData}
            isUploading={trialHandlers.isUploading}
            isSaving={trialHandlers.isSaving}
            updateRowsStatus={trialHandlers.updateRowsStatus}
            predicate={trialsResponse.predicate}
            deleteTableData={trialHandlers.deleteTableData}
            isDeleting={trialHandlers.isDeleting}
            onAddRow={addTrial}
            lineOptions={lineOptions}
            kitchenOptions={kitchenOptions}
            // The week's downtime, so a save can warn when a trial lands on
            // hours its line or kitchen is already down.
            downtimeRows={data as WorkstationDownTimeRowType[]}
          />
        ) : (
          <SchedulerWorkstationDownTimeTable
            data={data}
            isLoading={isLoading}
            updateTableData={updateTableData}
            uploadScheduleData={uploadScheduleData}
            isUploading={isUploading}
            isSaving={isSaving}
            updateRowsStatus={updateRowsStatus}
            predicate={predicate}
            deleteTableData={deleteTableData}
            isDeleting={isDeleting}
            // Identifies the schedule version a downtime comment belongs to.
            schedulerId={schedulerId}
            iteration={iteration}
            selectedWeek={selectedWeek}
          />
        )}
      </Box>
    </Box>
  )
}
